import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, type Breakpoint, ButtonBase } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type CSSProperties, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { NurseReasonMenu } from "../../../../components/nurseReasonMenu/nurseReasonMenu";
import { DoubleClickDeleteIcon } from "../../../../components/UIToolkit/Clickable/DoubleClickDeleteIcon";
import { SystemDataGrid } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useCallOffSourceLabel } from "../../../../hooks/useCallOffSourceLabel";
import { CALL_INS_PAGE_SIZE } from "../../../../hooks/useGetLimitedCallOffs";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { profileCallInsTableSortingKey } from "../../../../services/LocalStorageService";
import type { CalledOffShift, Nurse } from "../../../../utils/models";
import type { MultiSortOption, SortingState } from "../../../../utils/multiColumnSorting";
import { EditRemovalMessage } from "./EditRemovalMessage";
import { useLastCallInsState } from "./useLastCallInsState";

const GRID_MAX_HEIGHT: Partial<Record<Breakpoint, CSSProperties["maxHeight"]>> = {
	xs: "300px",
	xl: "500px",
};

const messageCompare = (a: CalledOffShift, b: CalledOffShift): 1 | 0 | -1 => {
	const aVal = a.message ?? "";
	const bVal = b.message ?? "";
	if (aVal === bVal) return 0;
	return aVal < bVal ? -1 : 1;
};

type LastCallInsProps = { nurse: Nurse; limitRows?: number };

export const LastCallIns: FC<LastCallInsProps> = ({ nurse, limitRows = CALL_INS_PAGE_SIZE }) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const callOffSourceLabel = useCallOffSourceLabel();
	const {
		isLoadingGetLimitedCallOffs,
		rawData,
		handleNurseInShiftClick,
		clickedNurseInShift,
		handleMenuClose,
		editMessageType,
		setEditMessageType,
		freeTextMessage,
		handleCustomMessageChange,
		removeNurseLoading,
		handleDiscardNurseRemove,
		cancelCallOffLoading,
		removeCallOffId,
		isErrorGetLimitedCallOffs,
		updateCallOff,
	} = useLastCallInsState({ limitRows, nurseIds: [nurse.id] });

	const columns = useMemo<GridColDef<CalledOffShift>[]>(
		() => [
			{
				field: "startTime",
				headerName: t("shift.start"),
				flex: 1,
				minWidth: 180,
				renderCell: ({ row }) => row.startTime.toFormat("ccc, MMM d, yyyy HH:mm"),
			},
			{
				field: "deletedAt",
				headerName: t("call.in.removal.date"),
				flex: 1,
				minWidth: 180,
				renderCell: ({ row }) => row.deletedAt?.toFormat("ccc, MMM d, yyyy HH:mm"),
			},
			{
				field: "deletedSource",
				headerName: t("source"),
				width: 110,
				sortable: false,
				renderCell: ({ row }) => callOffSourceLabel(row.deletedSource),
			},
			{
				field: "message",
				headerName: t("reason"),
				flex: 1,
				minWidth: 160,
				renderCell: ({ row }) => (
					<ButtonBase
						onClick={(event) => handleNurseInShiftClick(row, event.currentTarget)}
						sx={{ width: "100%", justifyContent: "space-between", px: 1, height: "100%", textAlign: "left" }}
						data-testid={`call-in-reason-${row.id}`}
					>
						<InHouseTooltip title={row.message} disableHoverListener={!row.message}>
							<Box component="span" sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{row.message ?? t("call.in.no.reason.provided")}
							</Box>
						</InHouseTooltip>
						<ExpandMoreIcon fontSize="small" />
					</ButtonBase>
				),
			},
			{
				field: "removalMessage",
				headerName: t("message"),
				flex: 1.5,
				minWidth: 200,
				sortable: false,
				renderCell: ({ row }) => <EditRemovalMessage key={`removal_message_${row.id}`} callOff={row} updateCallOff={updateCallOff} />,
			},
			{
				field: "actions",
				headerName: t("actions"),
				width: 120,
				sortable: false,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => (
					<InHouseTooltip title={t("call.in.cancel.tooltip", { name: formatPersonName(nurse) })}>
						<DoubleClickDeleteIcon
							isLoading={cancelCallOffLoading && removeCallOffId === row.id}
							opacityWhenNotHovered={1}
							onDeleteConfirmed={() => handleDiscardNurseRemove(row)}
						/>
					</InHouseTooltip>
				),
			},
		],
		[
			t,
			callOffSourceLabel,
			handleNurseInShiftClick,
			updateCallOff,
			cancelCallOffLoading,
			removeCallOffId,
			handleDiscardNurseRemove,
			nurse.firstName,
			nurse.lastName,
		],
	);

	const sortingOptions = useMemo<MultiSortOption<CalledOffShift>[]>(
		() => [
			{ label: t("shift.start"), sortColumns: ["startTime"] },
			{ label: t("call.in.removal.date"), sortColumns: ["deletedAt"] },
			{ label: t("reason"), sortColumns: ["message"], customSortFunction: messageCompare },
		],
		[t],
	);

	const defaultSortState = useMemo<SortingState<CalledOffShift>[]>(() => [{ sortColumns: ["deletedAt"], sortOrder: "desc" }], []);

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
			<Box sx={{ width: "100%", height: GRID_MAX_HEIGHT, display: "flex", flexDirection: "column" }}>
				<SystemDataGrid<CalledOffShift>
					rows={rawData}
					columns={columns}
					getRowId={(row) => row.id}
					sortingOptions={sortingOptions}
					defaultSortState={defaultSortState}
					localStorageKey={profileCallInsTableSortingKey}
					loading={isLoadingGetLimitedCallOffs}
					error={isErrorGetLimitedCallOffs}
					dataTestId={"profile-call-ins"}
				/>
			</Box>
			{clickedNurseInShift && (
				<NurseReasonMenu
					anchorEl={clickedNurseInShift.anchorEl}
					menuOpen={true}
					handleMenuClose={handleMenuClose}
					editMessage={editMessageType}
					setEditMessage={setEditMessageType}
					customMessage={freeTextMessage}
					handleEditMessageChange={handleCustomMessageChange}
					isLoading={removeNurseLoading}
				/>
			)}
		</Box>
	);
};
